import { useState } from 'react';
import { useConsoleServices } from './console-services.jsx';
import type { StepViewProps } from './flow-route-types.js';
import { firstIncompleteStep } from './gating.js';
import { Notice } from './notice.jsx';
import { onlyDigits } from './onboarding-state.js';
import { paths } from './paths.js';
import { useOnboarding } from './use-onboarding.jsx';
import { useWhatsappConnection } from './use-whatsapp-connection.js';

export function StepActivate({ route, steps, onNavigate }: StepViewProps) {
  const { draft, connection } = useOnboarding();
  const services = useConsoleServices();
  const controller = useWhatsappConnection(services);
  const pendingStep = firstIncompleteStep(
    steps.filter((step) => step.path !== route.path),
    { draft, connection },
  );

  if (connection?.status === 'activated') {
    return <Activated displayPhoneNumber={connection.displayPhoneNumber} onNavigate={onNavigate} />;
  }

  if (pendingStep) {
    return (
      <div className="flex flex-col gap-6">
        <Notice tone="warning" title="Ainda falta uma etapa">
          Complete <strong>{pendingStep.title}</strong> antes de iniciar o teste grátis.
        </Notice>
        <div>
          <button className="btn btn-primary" type="button" onClick={() => onNavigate(pendingStep.path)}>
            Ir para {pendingStep.title}
          </button>
        </div>
      </div>
    );
  }

  if (controller.activationStatus === 'ativando') {
    return (
      <p className="flex items-center gap-3" role="status">
        <span className="loading loading-spinner loading-sm" aria-hidden="true" />
        Iniciando seu teste grátis…
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ActivationError message={controller.activationError} />
      <p>
        Você vai iniciar 7 dias de teste grátis no número{' '}
        <strong className="font-mono">{connection?.displayPhoneNumber ?? '—'}</strong>.
      </p>
      <div>
        <button className="btn btn-primary" type="button" onClick={() => void controller.activate()}>
          Iniciar teste grátis de 7 dias
        </button>
      </div>
    </div>
  );
}

function ActivationError({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }

  return (
    <Notice tone="danger" title="Não foi possível ativar">
      {message}
    </Notice>
  );
}

function Activated({
  displayPhoneNumber,
  onNavigate,
}: {
  displayPhoneNumber: string | null;
  onNavigate: (path: string) => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      <Notice tone="success" title="Teste grátis ativo">
        Seu WhatsApp oficial está conectado e seus 7 dias de teste começaram.
      </Notice>
      <WhatsappLink displayPhoneNumber={displayPhoneNumber} />
      <div>
        <button className="btn btn-primary" type="button" onClick={() => onNavigate(paths.dashboard)}>
          Ir para o dashboard
        </button>
      </div>
    </div>
  );
}

export function WhatsappLink({ displayPhoneNumber }: { displayPhoneNumber: string | null }) {
  const [copied, setCopiado] = useState(false);

  if (!displayPhoneNumber) {
    return null;
  }

  const link = `https://wa.me/${onlyDigits(displayPhoneNumber)}`;

  async function copy() {
    await navigator.clipboard.writeText(link);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <div className="rounded-box border border-base-300 p-4">
      <p className="text-sm opacity-60">Link para seus clientes</p>
      <a className="link link-primary font-mono break-all" href={link} target="_blank" rel="noreferrer">
        {link}
      </a>
      <div className="mt-3 flex items-center gap-3">
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => void copy()}>
          Copiar link
        </button>
        <CopyFeedback copied={copied} />
      </div>
    </div>
  );
}

function CopyFeedback({ copied }: { copied: boolean }) {
  if (!copied) {
    return null;
  }

  return (
    <span className="text-sm text-success" role="status">
      Link copied
    </span>
  );
}
